/**
 * Route publique `/animation/$slug` — une animation publiée (lot 4), sans
 * compte : lien de partage, lien des Shorts / TikTok.
 *
 * La partie gratuite se joue pour tous ; la partie « À toi » (questions,
 * outils) seulement avec l'accès à la SKILLCARD source — le serveur ne l'envoie
 * pas sinon (`get_animation`), et « Débloquer » mène à la page de la carte
 * (offres, connexion ; rien à acheter dans l'app iOS, App Store 3.1.1).
 * Haut d'écran natif : réservé par la racine (`needsTopSafeArea`).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Check, Layers, Share2 } from 'lucide-react';
import { stripMath } from '@jeraisonne/db/cards';
import { CompassLogo } from '@jeraisonne/ui';
import {
  AnimationLoading,
  AnimationNotice,
  PublishedAnimationPlayer,
} from '@/features/animations/published-animation';
import { useAuth } from '@/lib/auth';
import { animationShareUrl, usePublishedAnimation } from '@/lib/animations/published';
import { shareContent } from '@/lib/native-share';

function ShareButton({ slug, title }: { slug: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        aria-label="Partager l’animation"
        title="Partager"
        onClick={async () => {
          setFailed(false);
          const r = await shareContent({
            title: `Animation « ${title} »`,
            text: `Regarde l’animation « ${title} » sur JeRaisonne !`,
            url: animationShareUrl(slug),
          });
          if (r === 'copied') {
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
          } else if (r === 'failed') setFailed(true);
        }}
        className="border-ink-200 text-ink-700 hover:bg-ink-50 flex h-11 w-11 items-center justify-center rounded-full border bg-white active:scale-[0.97]"
      >
        {copied ? (
          <Check className="h-5 w-5" aria-hidden />
        ) : (
          <Share2 className="h-5 w-5" aria-hidden />
        )}
      </button>
      <span role="status" className="text-ink-500 min-h-4 text-xs">
        {copied ? 'Lien copié' : failed ? 'Partage indisponible' : ''}
      </span>
    </div>
  );
}

export function PublicAnimationPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  // session pas encore lue : pas d'appel anonyme suivi d'un second, connecté
  const query = usePublishedAnimation(slug, session?.user?.id ?? null, !loading);
  const data = query.data;

  useEffect(() => {
    if (data) document.title = `${stripMath(data.title)} · JeRaisonne`;
  }, [data]);

  return (
    <div className="min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-6">
      <div className="mx-auto max-w-xl space-y-3">
        <div className="flex items-start justify-between gap-3">
          <Link
            to="/"
            aria-label="JeRaisonne — accueil"
            className="inline-flex min-h-11 items-center gap-2"
          >
            <CompassLogo size="sm" animated={false} showWordmark={false} />
            <span className="font-display text-ink-900 text-base font-extrabold leading-none">
              JeRaisonne <span className="text-ink-500 text-sm font-semibold">animation</span>
            </span>
          </Link>
          {data && <ShareButton slug={data.slug} title={stripMath(data.title)} />}
        </div>

        {/* le titre est dit et montré par le lecteur (écran de départ, intro) */}
        {data && <h1 className="sr-only">{stripMath(data.title)}</h1>}

        {query.isPending && <AnimationLoading />}
        {query.error && (
          <AnimationNotice
            tone="error"
            action={
              <button
                type="button"
                onClick={() => void query.refetch()}
                disabled={query.isFetching}
                className="bg-primary-600 min-h-11 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-60"
              >
                {query.isFetching ? 'Chargement…' : 'Réessayer'}
              </button>
            }
          >
            Impossible de charger l’animation. Vérifie ta connexion et réessaie.
          </AnimationNotice>
        )}
        {query.data === null && (
          <AnimationNotice
            action={
              <Link
                to="/"
                className="text-primary-600 inline-flex min-h-11 items-center text-sm font-semibold"
              >
                Découvrir JeRaisonne
              </Link>
            }
          >
            Cette animation n’est pas (ou plus) disponible.
          </AnimationNotice>
        )}

        {data && (
          <>
            <PublishedAnimationPlayer
              key={`${data.version_no}-${data.access.interactive}`}
              data={data}
              onUnlock={
                data.card_id
                  ? () => void navigate({ to: '/card/$cardId', params: { cardId: data.card_id! } })
                  : undefined
              }
            />
            {data.card_id && (
              <Link
                to="/card/$cardId"
                params={{ cardId: data.card_id }}
                data-testid="animation-card-link"
                className="border-ink-200 hover:bg-ink-50 flex min-h-14 items-center gap-3 rounded-2xl border bg-white p-3"
              >
                <span className="bg-primary-50 text-primary-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                  <Layers className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-display text-ink-900 block text-[15px] font-bold">
                    Voir la SKILLCARD
                  </span>
                  <span className="text-ink-500 block text-[13px]">
                    {data.locked.length > 0
                      ? 'Le cours, les exercices et la partie « À toi » de cette animation'
                      : 'Le cours et les exercices pour t’entraîner'}
                  </span>
                </span>
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
